import React, { useEffect, useRef, useState } from 'react';
import Chart from 'chart.js/auto';
import { Box } from '@mui/material';

const UserGrowthChart = () => {
    const chartRef = useRef(null);
    const containerRef = useRef(null);
    const chartInstance = useRef(null);
    const [chartWidth, setChartWidth] = useState(4500);

    useEffect(() => {
        const chartCanvas = chartRef.current.getContext('2d');

        if (chartInstance.current) {
            chartInstance.current.destroy();
        }

        const backgroundGradient = chartCanvas.createLinearGradient(0, 0, 0, 500);
        backgroundGradient.addColorStop(0, 'rgba(229, 238, 250, 1)');  // Corrected!
        backgroundGradient.addColorStop(1, 'rgba(229, 238, 250, 0)');

        const data = {
            labels: [
                'Jan 2024', 'Feb 2024', 'Mar 2024', 'Apr 2024', 'May 2024', 'Jun 2024',
                'Jul 2024', 'Aug 2024', 'Sep 2024', 'Oct 2024', 'Nov 2024', 'Dec 2024',
            ],
            datasets: [
                {
                    label: 'Users',
                    data: [3750, 3600, 3250, 3450, 3000, 3400, 3550, 3150, 3100, 2900, 3800, 3250],
                    fill: true,
                    backgroundColor: backgroundGradient,
                    borderColor: 'rgba(229, 238, 250, 1)',
                    borderWidth: 2,
                    pointRadius: 5,
                    pointBackgroundColor: 'rgba(0, 123, 255, 1)',
                    pointBorderColor: '#fff',
                    pointBorderWidth: 1,
                    tension: 0.3, // For smoother curves
                },
            ],
        };

        const options = {
            responsive: true,
            maintainAspectRatio: true,
            scales: {
                y: {
                    beginAtZero: false,
                    min: 1000,
                    max: 5000,
                    ticks: {
                        stepSize: 1000,
                        callback: function (value) {
                            return value >= 1000 ? value / 1000 + 'K' : value;
                        },
                        color: '#666',
                    },
                    grid: {
                        display: false,
                        color: 'rgba(0, 0, 0, 0.05)',
                    },
                },
                x: {
                    grid: {
                        display: false,
                    },
                    ticks: {
                        color: '#666',
                    },
                },
            },
            plugins: {
                legend: {
                    display: false, // Hide the legend
                },
                tooltip: {
                    mode: 'index',
                    intersect: false,
                    callbacks: {
                        label: function (context) {
                            let label = context.dataset.label || '';
                            if (label) {
                                label += ': ';
                            }
                            if (context.parsed.y !== null) {
                                label += context.parsed.y + ' Users';
                            }
                            return label;
                        },
                    },
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    titleColor: '#fff',
                    bodyColor: '#fff',
                    borderColor: 'rgba(0, 123, 255, 1)',
                    borderWidth: 1,
                    cornerRadius: 3,
                    padding: 8,
                },
                annotation: {
                    annotations: [
                        {
                            type: 'point',
                            xValue: 'May 2024',
                            yValue: 3000,
                            pointRadius: 8,
                            pointBackgroundColor: 'rgba(0, 123, 255, 1)',
                            pointBorderColor: '#fff',
                            pointBorderWidth: 1,
                        },
                        {
                            type: 'label',
                            xValue: 'May 2024',
                            yValue: 3000,
                            content: '3K Users',
                            backgroundColor: 'rgba(0, 0, 0, 0.6)',
                            color: '#fff',
                            font: {
                                size: 12,
                            },
                            padding: 6,
                            borderRadius: 3,
                            textAlign: 'center',
                            yAdjust: -15,
                        },
                        {
                            type: 'point',
                            xValue: 'Jun 2024',
                            yValue: 3400,
                            pointRadius: 8,
                            pointBackgroundColor: 'rgba(0, 123, 255, 1)',
                            pointBorderColor: '#fff',
                            pointBorderWidth: 1,
                        },
                        {
                            type: 'label',
                            xValue: 'Jun 2024',
                            yValue: 3400,
                            content: '3.4K Users',
                            backgroundColor: 'rgba(0, 0, 0, 0.6)',
                            color: '#fff',
                            font: {
                                size: 12,
                            },
                            padding: 6,
                            borderRadius: 3,
                            textAlign: 'center',
                            yAdjust: -15,
                        },
                    ],
                },
            },
        };

        chartInstance.current = new Chart(chartCanvas, {
            type: 'line',
            data: data,
            options: options,
            plugins: [
                {
                    id: 'annotation',
                    beforeDraw: (chart) => {
                        const annotations = chart.options.plugins.annotation.annotations;
                        annotations.forEach((annotation) => {
                            if (annotation.content && annotation.type === 'label') {
                                const pointIndex = chart.data.labels.indexOf(annotation.xValue);
                                if (pointIndex !== -1 && chart.data.datasets[0].data[pointIndex] === annotation.yValue) {
                                    const pointMeta = chart.getDatasetMeta(0).data[pointIndex];
                                    if (pointMeta && pointMeta.x && pointMeta.y) {
                                        annotation.x = pointMeta.x;
                                        annotation.y = pointMeta.y + annotation.yAdjust;
                                    }
                                }
                            }
                        });
                    },
                },
            ],
        });

        return () => {
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }
        };
    }, []);

    useEffect(() => {
        const updateChartWidth = () => {
            if (containerRef.current) {

                const containerWidth = containerRef.current.offsetWidth - 3;
                setChartWidth(containerWidth); // Set the width to the container's width
            }
        };

        // Initial width calculation
        updateChartWidth();

        // Update width on window resize
        window.addEventListener('resize', updateChartWidth);
        return () => {
            window.removeEventListener('resize', updateChartWidth);
        };
    }, [containerRef]);



    return <Box ref={containerRef} sx={{ width: '100%' }}><canvas ref={chartRef} width={chartWidth} height={1500} style={{ width: '100%', height: 'auto' }} /></Box>;
};

export default UserGrowthChart;